import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";

import { Context, LOCALE, Settings, getBuildVersion } from "@hermes/app-core";
import {
    AnalyticsService,
    EVENT_UAEVENT,
    GTMClientGender,
    getAnalyticsDeviceType,
    getUserType,
} from "@hermes/utils/analytics";
import { Locale } from "@hermes/locale";
import { UserFacade } from "@hermes/states/user";
import { UrlUtils } from "@hermes/utils-generic/services/user-interface";

interface SearchAgecResultEventData {
    eventaction: string;
    eventlabel: string;
}

@Injectable()
export class AgecPageAnalyticsService implements OnDestroy {
    private subscription = new Subscription();

    constructor(
        private context: Context,
        private analyticsService: AnalyticsService,
        private settings: Settings,
        private userFacade: UserFacade,
        private urlUtils: UrlUtils,
        @Inject(DOCUMENT) private document: Document,
        @Inject(LOCALE) private locale: Locale,
    ) {}

    public sendAgecPageViewEvent(): void {
        if (!this.context.isInBrowserMode()) {
            return;
        }

        this.subscription.add(
            this.userFacade.userIsSynchronized$
                .pipe(
                    filter((isSynchro) => isSynchro),
                    switchMap(() => this.userFacade.userDetails$),
                    take(1),
                )
                .subscribe((user) => {
                    this.analyticsService.sendRawData({
                        event: "pageview",
                        pagename: this.document.title,
                        pagetype: "environment",
                        buildversion: getBuildVersion(this.settings.envKey),
                        devicetype: getAnalyticsDeviceType(),
                        languagecode: this.locale.langCode,
                        countrycode: this.locale.countryCode,
                        userId: undefined,
                        clientgender: user.prefix as GTMClientGender,
                        clienttype: getUserType(user.userType),
                        previousPage: this.urlUtils.getPreviousUrl(),
                    });
                }),
        );
    }

    public sendVirtualPageViewEvent(): void {
        this.analyticsService.sendRawData({
            event: "virtualPageview",
            virtualpageuri: "/environment/product-description",
        });
    }

    public sendUAEvent(eventData: SearchAgecResultEventData): void {
        this.analyticsService.sendRawData({
            event: EVENT_UAEVENT,
            eventcategory: "Environment",
            eventaction: eventData.eventaction,
            eventlabel: eventData.eventlabel,
        });
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
